/* FG Scent Profile block behavior: tap-to-reveal on touch, tier explainer and
   How it wears panel disclosure (the title or anywhere on a compact card), the longevity line's scale sheets
   (native <dialog>), and a one-time entrance animation. Progressive enhancement —
   the block is fully readable with this script absent (see fg-scent-profile.css:
   entrance styles only apply once `.js` is added to the root element). */
(function () {
  if (customElements.get('fg-scent-profile')) return;

  class FgScentProfile extends HTMLElement {
    connectedCallback() {
      this.classList.add('js');
      this.setupDescriptionClamp();
      if (this.fgInitialised) return;
      this.fgInitialised = true;

      this.querySelectorAll('dialog.fg-wsheet').forEach((dialog) => {
        dialog.addEventListener('cancel', (e) => {
          e.preventDefault();
          this.closeSheet(dialog);
        });
      });

      this.addEventListener('click', (e) => {
        const sheetOpener = e.target.closest('[data-fg-sheet]');
        if (sheetOpener) {
          this.openSheet(document.getElementById(sheetOpener.getAttribute('aria-controls')), sheetOpener);
          return;
        }
        if (e.target.closest('[data-fg-sheet-close]') || e.target.matches('dialog.fg-wsheet')) {
          this.closeSheet(e.target.closest('dialog.fg-wsheet'));
          return;
        }
        const descMore = e.target.closest('.fg-desc__more');
        if (descMore) {
          this.toggleDescription(descMore);
          return;
        }
        if (
          e.target.closest('.fg-desc__body') &&
          this.classList.contains('fg-desc') &&
          !this.classList.contains('is-expanded') &&
          !this.classList.contains('is-fit') &&
          !e.target.closest('a')
        ) {
          const more = this.querySelector('.fg-desc__more');
          if (more) this.toggleDescription(more);
          return;
        }
        const toggle = e.target.closest('[aria-controls][aria-expanded]');
        if (toggle) {
          this.toggleDisclosure(toggle);
          return;
        }
        const card = e.target.closest('.fg-hiw--toggle');
        if (card && !e.target.closest('a, button') && !String(window.getSelection())) {
          const cardToggle = card.querySelector('.fg-hiw__title[aria-controls]');
          if (cardToggle) this.toggleDisclosure(cardToggle);
          return;
        }
        const bar = e.target.closest('.fg-acc');
        if (bar && !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          e.preventDefault();
          this.toggleBar(bar);
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeAllExplainers();
      });

      this.setupEntrance();
    }

    openSheet(dialog, trigger) {
      if (!dialog || dialog.open) return;
      this.sheetTrigger = trigger;
      dialog.showModal();
      document.documentElement.classList.add('fg-wsheet-lock');
      void dialog.offsetWidth;
      dialog.classList.add('is-open');
    }

    closeSheet(dialog) {
      if (!dialog || !dialog.open || dialog.dataset.closing) return;
      dialog.classList.remove('is-open');
      const finish = () => {
        delete dialog.dataset.closing;
        dialog.close();
        document.documentElement.classList.remove('fg-wsheet-lock');
        if (this.sheetTrigger) this.sheetTrigger.focus({ preventScroll: true });
        this.sheetTrigger = null;
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return finish();
      dialog.dataset.closing = 'true';
      setTimeout(finish, 380);
    }

    toggleBar(bar) {
      const wasOn = bar.classList.contains('is-on');
      this.querySelectorAll('.fg-acc.is-on').forEach((b) => b.classList.remove('is-on'));
      if (!wasOn) bar.classList.add('is-on');
    }

    toggleDisclosure(button) {
      const open = button.getAttribute('aria-expanded') !== 'true';
      if (button.classList.contains('fg-tier__label')) this.closeAllExplainers();
      this.setOpen(button, open);
    }

    setOpen(button, open) {
      button.setAttribute('aria-expanded', String(open));
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (panel) panel.hidden = !open;
    }

    closeAllExplainers() {
      this.querySelectorAll('[aria-controls][aria-expanded="true"]:not(.fg-desc__more)').forEach((b) => this.setOpen(b, false));
    }

    toggleDescription(button) {
      const desc = button.closest('.fg-desc');
      const body = desc && desc.querySelector('.fg-desc__body');
      if (!body) return;
      const open = !desc.classList.contains('is-expanded');
      const animate =
        desc.dataset.motion === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const label = button.querySelector('.fg-desc__more-label');

      button.setAttribute('aria-expanded', String(open));
      if (label) label.textContent = open ? button.dataset.less : button.dataset.more;

      const clearWhenDone = () => {
        body.style.maxHeight = '';
      };

      if (open) {
        body.style.maxHeight = `${body.getBoundingClientRect().height}px`;
        desc.classList.add('is-expanded');
        if (!animate) return clearWhenDone();
        void body.offsetHeight;
        body.style.maxHeight = `${body.scrollHeight}px`;
        body.addEventListener('transitionend', clearWhenDone, { once: true });
      } else {
        body.style.maxHeight = `${body.getBoundingClientRect().height}px`;
        void body.offsetHeight;
        desc.classList.remove('is-expanded');
        const clamp = this.descClampHeight();
        body.style.maxHeight = `${clamp}px`;
        if (animate) body.addEventListener('transitionend', clearWhenDone, { once: true });
        else clearWhenDone();

        const about = desc.closest('.fg-about') || desc;
        if (about.getBoundingClientRect().top < 0) {
          about.scrollIntoView({ block: 'start', behavior: animate ? 'smooth' : 'auto' });
        }
      }
    }

    descClampHeight() {
      return parseFloat(getComputedStyle(this).getPropertyValue('--fg-desc-clamp')) || 150;
    }

    setupDescriptionClamp() {
      if (!this.classList.contains('fg-desc')) return;
      const body = this.querySelector('.fg-desc__body');
      const button = this.querySelector('.fg-desc__more');
      if (!body || !button) return;
      const evaluate = () => {
        if (!body.getClientRects().length || this.classList.contains('is-expanded')) return;
        const fits = body.scrollHeight <= this.descClampHeight() + 4;
        this.classList.toggle('is-fit', fits);
        button.hidden = fits;
      };
      requestAnimationFrame(evaluate);
      if ('ResizeObserver' in window && !this.descObserver) {
        this.descObserver = new ResizeObserver(evaluate);
        this.descObserver.observe(body);
      }
    }

    setupEntrance() {
      const list = this.querySelector('.fg-acc-list[data-entrance="true"]');
      if (!list) return;
      if (!('IntersectionObserver' in window)) {
        list.classList.add('is-entering');
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              list.classList.add('is-entering');
              io.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -80px 0px' }
      );
      io.observe(list);
      if (list.getBoundingClientRect().top < window.innerHeight) {
        list.classList.add('is-entering');
        io.unobserve(list);
      }
    }
  }

  customElements.define('fg-scent-profile', FgScentProfile);
})();
